import { createContext, useContext, useMemo } from 'react'
import type { JSX } from 'react'
import type { ReactNode } from 'react'

import { getChannelColor, getMetricColor } from './palette'
import type { ChartSeries, ChartSeriesInput } from './types'

/**
 * Shares the chart's series registry — identity, display metadata, resolved
 * colour, and data — so the line, tooltip, legend, and data table all read one
 * source and can't drift. Every chart view is rendered inside a
 * {@link ChartSeriesProvider}; colour is assigned here (never passed as a prop).
 */
interface ChartSeriesContextValue {
  /** Series in registration order (primaries and their comparison siblings). */
  series: ChartSeries[]
  /** Fast lookup by series id. */
  byId: Map<string, ChartSeries>
}

const ChartSeriesContext = createContext<ChartSeriesContextValue | null>(null)

/** A comparison sibling shares its primary's colour; strip the suffix to match. */
const baseId = (id: string): string => id.replace(/-comparison$/, '')

function resolveSeries(inputs: ChartSeriesInput[]): ChartSeries[] {
  // Assign a positional palette slot to each primary base in registration order,
  // so comparison siblings (and repeat references) inherit the same colour.
  const autoIndexByBase = new Map<string, number>()
  let nextIndex = 0
  for (const input of inputs) {
    const base = baseId(input.id)
    if (input.colorRole == null && !autoIndexByBase.has(base)) {
      autoIndexByBase.set(base, nextIndex++)
    }
  }

  return inputs.map((input) => {
    const kind =
      input.kind ??
      (input.id.endsWith('-comparison') ? 'comparison' : 'primary')
    const color =
      input.colorRole != null
        ? getMetricColor(input.colorRole)
        : getChannelColor(
            input.paletteIndex ?? autoIndexByBase.get(baseId(input.id)) ?? 0,
          )
    return { ...input, kind, color }
  })
}

interface ChartSeriesProviderProps {
  series: ChartSeriesInput[]
  children: ReactNode
}

export function ChartSeriesProvider({
  series,
  children,
}: ChartSeriesProviderProps): JSX.Element {
  const value = useMemo<ChartSeriesContextValue>(() => {
    const resolved = resolveSeries(series)
    return {
      series: resolved,
      byId: new Map(resolved.map((s) => [s.id, s])),
    }
  }, [series])

  return (
    <ChartSeriesContext.Provider value={value}>
      {children}
    </ChartSeriesContext.Provider>
  )
}

function useChartSeriesContext(): ChartSeriesContextValue {
  const value = useContext(ChartSeriesContext)
  if (value == null) {
    throw new Error(
      'useChartSeries must be used within a <ChartSeriesProvider>. Wrap the chart view in a provider.',
    )
  }
  return value
}

/** The full ordered series registry (resolved colours included). */
export function useChartSeries(): ChartSeries[] {
  return useChartSeriesContext().series
}

/** A single resolved series by id, or `undefined` if not registered. */
export function useChartSeriesById(id: string): ChartSeries | undefined {
  return useChartSeriesContext().byId.get(id)
}
